import { logger } from "./logger"

/**
 * "You are not connected", with a way back.
 *
 * Deliberately DOM rather than pixi. This is the screen for when things are already broken -- the
 * server is down, the wifi dropped, the app was asleep for an hour -- and the renderer is exactly
 * the thing that may be mid-collapse or never started. A div needs nothing but a document.
 *
 * It exists because the native shell has nowhere to go. In a browser a lost socket redirects to the
 * portal, but the shell's origin IS the app bundle: "/" would reload the app, and an alert() would
 * freeze the WebView behind a modal the player cannot dismiss into anything useful. So the shell
 * sat there silently on a dead socket, which looks exactly like the game having hung.
 */

const OVERLAY_ID = "samsara-disconnected"

export interface DisconnectedProps {
    /** True if the socket had ever been open -- "lost" reads differently from "never reached". */
    everConnected: boolean
    /** Re-attempt the connection. The overlay stays up; it comes down when the socket opens. */
    onRetry: () => void
}

const style = (el: HTMLElement, css: Record<string, string>) => {
    Object.entries(css).forEach(([key, value]) => el.style.setProperty(key, value))
}

export const isDisconnectedShowing = (): boolean =>
    typeof document !== "undefined" && !!document.getElementById(OVERLAY_ID)

export const hideDisconnected = () => {
    if (typeof document === "undefined") {
        return
    }
    document.getElementById(OVERLAY_ID)?.remove()
}

/**
 * The same card for a session the server ENDED on purpose -- it said so before closing, and the
 * reason is why there is no "Try again" here.
 *
 * "Signed in somewhere else" is the one that happens: an account holds one session, so logging in
 * again anywhere takes it off this window. Retrying would be a tug of war between the two. The way
 * back is a deliberate log in, which is what the reload gives.
 */
export const showSessionEnded = (reason: string) => {
    if (typeof document === "undefined") {
        return
    }
    // Takes over from the generic panel if a close got here first: this is the more specific story,
    // and the one with no retry.
    hideDisconnected()
    const signedInElsewhere = reason === "signedInElsewhere"
    // Sent by the /admin Players view's Kick. Not a ban: logging in again works.
    const kicked = reason === "kicked"
    const contentMismatch = ["contentRevisionMismatch", "contentActivationFailed", "contentActivationTimeout"].includes(reason)
    renderOverlay({
        heading: contentMismatch ? "Game update required" : signedInElsewhere ? "Signed in somewhere else" : kicked ? "Removed from the game" : "Session ended",
        body: contentMismatch ? "Your game content differs from the server. Reload to get the current version." : signedInElsewhere
            ? "This account was signed in from another window or device, so this one has been signed out."
            : kicked ? "An administrator ended your session."
            : "The server ended this session.",
        buttonLabel: contentMismatch ? "Reload game" : "Log in again",
        onPress: () => window.location.reload(),
        // A reload leaves, so there is nothing to re-arm.
        rearm: false,
    })
}

export const showDisconnected = ({ everConnected, onRetry }: DisconnectedProps) => {
    if (typeof document === "undefined" || isDisconnectedShowing()) {
        // Already up: a dead socket can report itself more than once (an error, then a close), and
        // the player should not watch the panel flicker or lose a retry they are mid-press on.
        return
    }

    renderOverlay({
        heading: everConnected ? "Connection lost" : "Can't reach the server",
        body: everConnected
            ? "You have been disconnected from the world."
            : "The server may be down, or this device may be offline.",
        buttonLabel: "Try again",
        onPress: onRetry,
        rearm: true,
    })
}

interface OverlayProps {
    heading: string
    body: string
    buttonLabel: string
    onPress: () => void
    /** Whether the button comes back after a press (a retry does; a reload does not). */
    rearm: boolean
}

const renderOverlay = ({ heading: headingText, body: bodyText, buttonLabel, onPress, rearm }: OverlayProps) => {
    if (isDisconnectedShowing()) {
        return
    }

    const overlay = document.createElement("div")
    overlay.id = OVERLAY_ID
    style(overlay, {
        position: "fixed",
        inset: "0",
        // Not fully opaque: the world behind stays faintly visible, so this reads as something
        // laid over the game rather than as the app having quit.
        background: "rgba(7, 9, 13, 0.92)",
        display: "flex",
        "align-items": "center",
        "justify-content": "center",
        "font-family": "'Jost', Verdana, Geneva, sans-serif",
        "z-index": "20",
        padding: "16px",
        "box-sizing": "border-box",
    })

    const card = document.createElement("div")
    style(card, {
        width: "min(380px, 100%)",
        "box-sizing": "border-box",
        padding: "26px",
        background: "rgba(17, 21, 27, 0.95)",
        border: "1px solid rgba(122, 136, 158, 0.45)",
        "border-radius": "10px",
        "text-align": "center",
    })

    const heading = document.createElement("div")
    heading.textContent = headingText
    style(heading, {
        "font-weight": "300",
        "font-size": "22px",
        "letter-spacing": "0.12em",
        color: "#f2f4ef",
    })

    const body = document.createElement("div")
    body.textContent = bodyText
    style(body, {
        "margin-top": "10px",
        "font-size": "14px",
        "line-height": "1.45",
        color: "#a9b2bf",
    })

    const button = document.createElement("button")
    button.textContent = buttonLabel
    style(button, {
        "margin-top": "22px",
        width: "100%",
        // Tall enough to be a real touch target. It is the only control on the screen, and the
        // player is already unhappy by the time they are looking at it.
        height: "48px",
        background: "#8fd66b",
        border: "0",
        "border-radius": "8px",
        "font-family": "inherit",
        "font-size": "16px",
        "font-weight": "600",
        color: "#12210a",
        cursor: "pointer",
    })

    let pressed = false
    const press = () => {
        if (pressed) {
            return
        }
        pressed = true
        button.textContent = rearm ? "Reconnecting..." : "One moment..."
        button.disabled = true
        style(button, { background: "#5c7a49", color: "#0d1607", cursor: "default" })

        try {
            onPress()
        } catch (e) {
            logger.error({ tags: ["websocket", "connection"] }, "Retry threw", String(e))
        }

        if (!rearm) {
            return
        }

        // A refused connection can fail faster than the player lets go of the button, so re-arming
        // from the close handler would race this. Re-arm on a timer instead: the control always
        // comes back, and a second press is always possible.
        setTimeout(() => {
            if (!isDisconnectedShowing()) {
                return
            }
            pressed = false
            button.textContent = buttonLabel
            button.disabled = false
            style(button, { background: "#8fd66b", color: "#12210a", cursor: "pointer" })
        }, 3000)
    }

    button.addEventListener("click", press)

    card.append(heading, body, button)
    overlay.append(card)
    document.body.append(overlay)
}
